export const numberWithCommas = (x: number, decimals: number = 0) => {
  const [intPart, decimalPart] = x.toFixed(decimals).split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimalPart ? `${formattedInt}.${decimalPart}` : formattedInt;
};

// MongoDB Long number type
export interface MongoLong {
  low: number;
  high: number;
  unsigned: boolean;
}

// Check if a value is a MongoDB Long number
export const isMongoLong = (value: unknown): value is MongoLong => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'low' in value &&
    'high' in value &&
    'unsigned' in value &&
    typeof (value as MongoLong).low === 'number' &&
    typeof (value as MongoLong).high === 'number' &&
    typeof (value as MongoLong).unsigned === 'boolean'
  );
};

// Convert MongoDB Long to BigInt (most precise)
// Formula: value = low + high * 2^32
//
// Example precision improvement:
// For large values like {low: -1754958392, high: 8038003, unsigned: false}:
// - Old method: -1754958392 + 8038003 * 2^32 ≈ 34,518,042,650,843,800,000 (loses precision)
// - New method: BigInt calculation preserves exact value
export const convertLongToBigInt = (value: MongoLong): bigint => {
  // Handle signed/unsigned interpretation
  const low = value.unsigned ? BigInt(value.low >>> 0) : BigInt(value.low | 0);
  const high = value.unsigned
    ? BigInt(value.high >>> 0)
    : BigInt(value.high | 0);

  return low + high * 2n ** 32n;
};

// Convert MongoDB Long to JavaScript number (with precision warning for large values)
// Note: May lose precision for values > Number.MAX_SAFE_INTEGER (2^53 - 1)
export const convertLongToNumber = (value: MongoLong | number): number => {
  if (typeof value === 'number') {
    return value;
  }

  if (isMongoLong(value)) {
    const bigIntValue = convertLongToBigInt(value);

    // Check if the value is safe to convert to Number
    if (bigIntValue > BigInt(Number.MAX_SAFE_INTEGER)) {
      console.warn(
        `Converting large MongoDB Long to Number may lose precision: ${bigIntValue.toString()}`,
      );
    }

    return Number(bigIntValue);
  }

  throw new Error('Invalid Long number format');
};

// Helper to safely convert any value that might be a Long or number
export const safeConvertToNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }

  if (isMongoLong(value)) {
    return convertLongToNumber(value);
  }

  return 0; // Default fallback
};

// Helper to get BigInt value for precise calculations
export const safeConvertToBigInt = (value: unknown): bigint => {
  if (typeof value === 'number') {
    return BigInt(value);
  }

  if (isMongoLong(value)) {
    return convertLongToBigInt(value);
  }

  return 0n; // Default fallback
};

// Utility for safe wei to ETH conversion using BigInt
export const weiToEthBigInt = (
  weiValue: MongoLong | number | bigint,
): string => {
  let wei: bigint;

  if (typeof weiValue === 'bigint') {
    wei = weiValue;
  } else if (typeof weiValue === 'number') {
    wei = BigInt(weiValue);
  } else if (isMongoLong(weiValue)) {
    wei = convertLongToBigInt(weiValue);
  } else {
    return '0';
  }

  // 1 ETH = 10^18 wei
  const ethBigInt = wei / 10n ** 18n;
  const remainderWei = wei % 10n ** 18n;

  // Convert to decimal string representation
  if (remainderWei === 0n) {
    return ethBigInt.toString();
  }

  // Handle decimal places
  const remainderStr = remainderWei.toString().padStart(18, '0');
  const trimmedRemainder = remainderStr.replace(/0+$/, '');

  return trimmedRemainder
    ? `${ethBigInt.toString()}.${trimmedRemainder}`
    : ethBigInt.toString();
};
